class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchTerm = '';
    this.renderedQuery = '';
    this.requestController = null;
    this.productToShow = Number(this.getAttribute('data-product-to-show')) || 8;
    this.setupEventListeners();
  }

  normaliseQuery(value = '') {
    return value.trim().replace(/\s+/g, ' ');
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (!form || !this.input) return;

    form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce((event) => this.onChange(event), 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.close(false);
      }
    });
  }

  getQuery() {
    return this.normaliseQuery(this.input?.value || '');
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    if (!newSearchTerm) {
      this.searchTerm = '';
      this.renderedQuery = '';
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    this.searchTerm = newSearchTerm;
    this.hideTrendingAndProducts();
    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    const selected = this.querySelector('#predictive-search-results-list [aria-selected="true"] a');

    if (!query) {
      event.preventDefault();
      return;
    }

    this.input.value = query;

    if (selected) {
      event.preventDefault();
      selected.click();
    }
  }

  onFocus() {
    const query = this.getQuery();
    if (!query) return;

    this.searchTerm = query;
    this.hideTrendingAndProducts();

    if (this.renderedQuery === query && this.getAttribute('results') === 'true') {
      this.open();
      return;
    }

    this.getSearchResults(query);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close(false);
      }
    }, 120);
  }

  onKeydown(event) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter') {
      const selected = this.querySelector(
        '#predictive-search-results-list [aria-selected="true"] a'
      );

      if (selected) {
        event.preventDefault();
        selected.click();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();

      if (this.isOpen) {
        this.close(false);
        this.showTrendingAndProducts();
        return;
      }

      const popup = this.closest('#halo-search-popup');
      const closeButton = popup?.querySelector('[data-search-close-popup], .header-search-close');
      closeButton?.click();
    }
  }

  onKeyup(event) {
    if (!this.getQuery()) {
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    if (event.key === 'ArrowUp') this.switchOption('up');
    if (event.key === 'ArrowDown') this.switchOption('down');
  }

  getSearchResults(searchTerm) {
    const query = this.normaliseQuery(searchTerm);
    if (!query || !this.predictiveSearchResults) return;

    const cacheKey = query.toLocaleLowerCase();
    const cached = this.cachedResults[cacheKey];

    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }

    this.setLiveRegionLoadingState();

    if (cached) {
      if (this.getQuery() === query) {
        this.renderSearchResults(cached, query);
      }
      return;
    }

    const controller = new AbortController();
    this.requestController = controller;
    this.setAttribute('loading', '');
    this.setAttribute('aria-busy', 'true');

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this.productToShow}&resources[options][unavailable_products]=last&section_id=predictive-search`,
      { signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== query) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup =
          html.querySelector('#shopify-section-predictive-search')?.innerHTML || '';

        this.cachedResults[cacheKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, query);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Predictive search request failed:', error);
        }
      })
      .finally(() => {
        if (this.requestController === controller) {
          this.requestController = null;
          this.removeAttribute('loading');
          this.removeAttribute('aria-busy');
        }
      });
  }

  setLiveRegionLoadingState() {
    const status = this.querySelector('.predictive-search-status');
    if (!status) return;

    status.setAttribute('aria-hidden', 'false');
    status.textContent = this.getAttribute('data-loading-text') || '';
    this.setAttribute('loading', '');
    this.setAttribute('aria-busy', 'true');
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.removeAttribute('aria-busy');

    const status = this.querySelector('.predictive-search-status');
    if (!status) return;

    status.setAttribute('aria-hidden', 'false');
    setTimeout(() => status.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup, query) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.renderedQuery = query;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  switchOption(direction) {
    const options = Array.from(
      this.querySelectorAll('#predictive-search-results-list [role="option"]')
    ).filter((option) => option.offsetParent !== null);

    if (!options.length) return;

    const selectedIndex = options.findIndex(
      (option) => option.getAttribute('aria-selected') === 'true'
    );

    let nextIndex;

    if (direction === 'down') {
      nextIndex = selectedIndex < options.length - 1 ? selectedIndex + 1 : 0;
    } else {
      nextIndex = selectedIndex > 0 ? selectedIndex - 1 : options.length - 1;
    }

    options.forEach((option) => option.setAttribute('aria-selected', 'false'));

    const next = options[nextIndex];
    next.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', next.id || '');
    next.scrollIntoView({ block: 'nearest' });
  }

  open() {
    if (!this.input) return;

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }

    this.removeAttribute('loading');
    this.removeAttribute('aria-busy');
    this.removeAttribute('open');

    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.searchTerm = '';
      this.renderedQuery = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector(
      '#predictive-search-results-list [aria-selected="true"]'
    );
    selected?.setAttribute('aria-selected', 'false');

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
    }

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    this.querySelectorAll('.quickSearchResultsWrap, .quickSearchResultsBlock').forEach((element) => {
      element.style.display = '';
    });
  }

  hideTrendingAndProducts() {
    this.querySelectorAll('.quickSearchResultsWrap, .quickSearchResultsBlock').forEach((element) => {
      element.style.display = 'none';
    });
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
